import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import ProductsShopifyTab from "./ProductsShopifyTab";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, Search, ImageOff, AlertCircle, CheckCircle2, X, Upload, Hash, Save, FileText, Trash2, Download, Eye, ChevronDown, Edit2, Send, Plus, ExternalLink, Image as ImageIcon, Video as VideoIcon, Link2, MapPin, Package as PackageIcon } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

function stockTotal(p) {
  // Source of truth = Oblio stock (`custom.stoc_oblio`). If the user has not
  // entered it yet, fall back to whatever Gomag reports so the column is
  // never blank.
  const oblio = (p?.custom || {}).stoc_oblio;
  if (oblio != null && oblio !== "") {
    const n = Number(oblio);
    if (Number.isFinite(n)) return n;
  }
  const s = p.stock;
  if (Array.isArray(s)) return s.reduce((sum, x) => sum + Number(x?.value || 0), 0);
  return Number(s || 0);
}

function ProductImage({ product, onUpload }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const src = product.main_image;

  const handle = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      await axios.post(`${API}/products/${product.id}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUpload();
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
    } finally { setUploading(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  return (
    <div className="group relative h-14 w-14 cursor-pointer overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900" onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }} title="Click pentru a încărca imagine">
      {src ? (
        <img src={src.startsWith("/api/") ? `${BACKEND}${src}` : src} alt={product.name} loading="lazy" className="h-full w-full object-cover" />
      ) : (
        <div className="flex h-full items-center justify-center text-zinc-700"><ImageOff className="h-5 w-5" /></div>
      )}
      <div className="absolute inset-0 hidden items-center justify-center bg-black/70 group-hover:flex">
        {uploading ? <RefreshCw className="h-4 w-4 animate-spin text-orange-300" /> : <Upload className="h-4 w-4 text-orange-300" />}
      </div>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handle} />
    </div>
  );
}

function ProductDocuments({ product }) {
  // Two PDF slots per product: CE (declaration of conformity) and MSDS
  // (material safety data sheet). The PDF lives in MongoDB so it survives
  // production redeploys; metadata is mirrored on `product.documents.{kind}`.
  const KINDS = [
    { key: "ce", label: "CE", desc: "Declarație de conformitate" },
    { key: "msds", label: "MSDS", desc: "Material Safety Data Sheet" },
    { key: "box", label: "Cutia produsului", desc: "PDF cu eticheta cutiei / packaging" },
    { key: "manual", label: "Manual produs", desc: "Manualul de utilizare PDF" },
  ];
  const [docs, setDocs] = useState(product?.documents || {});
  const [busy, setBusy] = useState({}); // { ce: "uploading"|"deleting" }
  const [error, setError] = useState("");
  const inputRefs = useRef({});

  // Coerce ANY error payload (string, Pydantic array, plain object) to a
  // user-readable string. Without this, FastAPI 422 responses (which return
  // `detail: [{type, loc, msg, input, url}]`) leak straight into JSX and
  // trigger React's "Objects are not valid as a React child" — white screen.
  const _humanizeError = (err) => {
    const detail = err?.response?.data?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
      return detail.map((d) => d?.msg || JSON.stringify(d)).join("; ");
    }
    if (detail && typeof detail === "object") {
      return detail.msg || JSON.stringify(detail);
    }
    return err?.message || "Eroare necunoscută";
  };

  useEffect(() => {
    // Reload docs metadata when modal opens — `product.documents` from the
    // list may be stale if the user uploaded one earlier in this session.
    let mounted = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/products/${product.id}/documents`);
        if (mounted) setDocs(r.data?.documents || {});
      } catch { /* keep whatever we had */ }
    })();
    return () => { mounted = false; };
  }, [product.id]);

  const onPick = (kind) => () => inputRefs.current[kind]?.click();

  const CHUNK_SIZE = 5 * 1024 * 1024;  // 5 MB per chunk — keeps every
                                       // request well under any reasonable
                                       // ingress `client_max_body_size`.

  const upload = (kind) => async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.pdf$/i.test(f.name) && f.type !== "application/pdf") {
      setError("Selectează un fișier PDF");
      e.target.value = "";
      return;
    }
    setBusy((b) => ({ ...b, [kind]: "uploading" }));
    setError("");
    try {
      // Small files: single request (fast path).
      if (f.size <= CHUNK_SIZE) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await axios.post(
          `${API}/products/${product.id}/documents/${kind}`,
          fd,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
        setDocs((d) => ({ ...d, [kind]: {
          filename: r.data.filename, size: r.data.size,
          uploaded_at: r.data.uploaded_at, url: r.data.url,
        } }));
      } else {
        // Large file: chunked upload sequentially. Session id lets the
        // backend reassemble the parts even if the browser retries.
        const sessionId = `${product.id}_${kind}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        const totalChunks = Math.ceil(f.size / CHUNK_SIZE);
        let last;
        for (let i = 0; i < totalChunks; i++) {
          const blob = f.slice(i * CHUNK_SIZE, Math.min((i + 1) * CHUNK_SIZE, f.size));
          const fd = new FormData();
          fd.append("session_id", sessionId);
          fd.append("index", String(i));
          fd.append("total", String(totalChunks));
          fd.append("filename", f.name);
          fd.append("file", blob, `chunk_${i}`);
          const r = await axios.post(
            `${API}/products/${product.id}/documents/${kind}/chunk`,
            fd,
            { headers: { "Content-Type": "multipart/form-data" } },
          );
          last = r.data;
          setBusy((b) => ({ ...b, [kind]: `uploading ${Math.round(((i + 1) / totalChunks) * 100)}%` }));
        }
        if (last && last.final) {
          setDocs((d) => ({ ...d, [kind]: {
            filename: last.filename, size: last.size,
            uploaded_at: last.uploaded_at, url: last.url,
          } }));
        }
      }
    } catch (err) {
      setError(_humanizeError(err));
    } finally {
      setBusy((b) => ({ ...b, [kind]: null }));
      if (inputRefs.current[kind]) inputRefs.current[kind].value = "";
    }
  };

  const remove = (kind, label) => async () => {
    if (!window.confirm(`Ștergi documentul ${label}?`)) return;
    setBusy((b) => ({ ...b, [kind]: "deleting" }));
    setError("");
    try {
      await axios.delete(`${API}/products/${product.id}/documents/${kind}`);
      setDocs((d) => {
        const next = { ...d };
        delete next[kind];
        return next;
      });
    } catch (err) {
      setError(_humanizeError(err));
    } finally {
      setBusy((b) => ({ ...b, [kind]: null }));
    }
  };

  const fmtSize = (n) => {
    if (!n) return "";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
  };
  const fmtDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString("ro-RO", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };

  return (
    <section className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4" data-testid="product-documents-section">
      <h4 className="mb-3 text-xs font-semibold uppercase tracking-wider text-zinc-400">Documente</h4>
      {error && (
        <div className="mb-2 flex items-start gap-2 rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5" /> {error}
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        {KINDS.map(({ key, label, desc }) => {
          const d = docs[key];
          const state = busy[key];
          return (
            <div key={key} className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-3" data-testid={`doc-row-${key}`}>
              <div className="mb-2 flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-zinc-200">{label}</p>
                  <p className="text-[10px] text-zinc-500">{desc}</p>
                </div>
                <FileText className={`h-4 w-4 ${d ? "text-emerald-300" : "text-zinc-700"}`} />
              </div>
              {d ? (
                <div className="space-y-2">
                  <div className="rounded-md bg-zinc-900/60 px-2 py-1.5">
                    <p className="truncate text-xs text-zinc-200" title={d.filename}>{d.filename}</p>
                    <p className="text-[10px] text-zinc-500">{fmtSize(d.size)} · {fmtDate(d.uploaded_at)}</p>
                  </div>
                  <div className="flex gap-1.5">
                    <a
                      href={`${BACKEND}/api/products/${product.id}/documents/${key}.pdf`}
                      target="_blank" rel="noreferrer"
                      data-testid={`doc-open-${key}`}
                      className="flex-1 inline-flex items-center justify-center gap-1 rounded-md border border-sky-500/40 bg-sky-500/10 px-2 py-1.5 text-xs text-sky-200 hover:bg-sky-500/20"
                    >
                      <Download className="h-3 w-3" /> Deschide
                    </a>
                    <button
                      type="button" onClick={onPick(key)} disabled={!!state}
                      data-testid={`doc-replace-${key}`}
                      className="inline-flex items-center gap-1 rounded-md border border-zinc-700 bg-zinc-900 px-2 py-1.5 text-xs text-zinc-300 hover:text-zinc-100 disabled:opacity-50"
                      title="Înlocuiește">
                      {state === "uploading" ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                    </button>
                    <button
                      type="button" onClick={remove(key, label)} disabled={!!state}
                      data-testid={`doc-delete-${key}`}
                      className="inline-flex items-center gap-1 rounded-md border border-rose-500/40 bg-rose-500/10 px-2 py-1.5 text-xs text-rose-200 hover:bg-rose-500/20 disabled:opacity-50"
                      title="Șterge">
                      {state === "deleting" ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button" onClick={onPick(key)} disabled={!!state}
                  data-testid={`doc-upload-${key}`}
                  className="flex w-full items-center justify-center gap-2 rounded-md border border-dashed border-zinc-700 bg-zinc-900/40 px-3 py-3 text-xs text-zinc-400 hover:border-orange-500/50 hover:text-orange-200 disabled:opacity-50"
                >
                  {state === "uploading" ? (
                    <><RefreshCw className="h-3.5 w-3.5 animate-spin" /> Se încarcă...</>
                  ) : (
                    <><Upload className="h-3.5 w-3.5" /> Încarcă PDF</>
                  )}
                </button>
              )}
              <input
                ref={(el) => { inputRefs.current[key] = el; }}
                type="file" accept="application/pdf,.pdf"
                className="hidden" onChange={upload(key)}
                data-testid={`doc-input-${key}`}
              />
            </div>
          );
        })}
      </div>
    </section>
  );
}

function ReadField({ label, value, testId, multiline, mono }) {
  return (
    <div>
      <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">{label}</label>
      <div data-testid={testId}
        className={`rounded-lg border border-zinc-800 bg-zinc-900/40 px-3 py-2 text-sm text-zinc-200 ${
          mono ? "font-mono" : ""
        } ${multiline ? "whitespace-pre-wrap break-words min-h-[2.5rem]" : "truncate"}`}>
        {value || <span className="text-zinc-600">—</span>}
      </div>
    </div>
  );
}

function QuickPrice({ product, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [saleVal, setSaleVal] = useState(product.pricing?.price_with_vat ?? product.price_with_vat ?? product.recommended_price ?? "");
  const [listVal, setListVal] = useState(product.pricing?.list_price_with_vat ?? "");
  const [busy, setBusy] = useState(false);
  const priceNoVat = product.pricing?.price_no_vat ?? product.price_no_vat ?? product.sale_price;
  const priceWithVat = product.pricing?.price_with_vat ?? product.price_with_vat ?? product.recommended_price;
  const listWithVat = product.pricing?.list_price_with_vat;
  const isGomag = product._source === "gomag";

  // Re-seed local state when the product prop refreshes (e.g. after onSaved
  // upstream refetches the list).
  useEffect(() => {
    setSaleVal(product.pricing?.price_with_vat ?? product.price_with_vat ?? product.recommended_price ?? "");
    setListVal(product.pricing?.list_price_with_vat ?? "");
  }, [product.pricing?.price_with_vat, product.pricing?.list_price_with_vat, product.recommended_price, product.price_with_vat]);

  const save = async () => {
    if (!saleVal || isNaN(Number(saleVal)) || Number(saleVal) <= 0) {
      alert("Preț REDUS (cu TVA) invalid.");
      return;
    }
    setBusy(true);
    try {
      const body = { price_with_vat: Number(saleVal) };
      if (listVal !== "" && !isNaN(Number(listVal)) && Number(listVal) > 0) {
        body.list_price_with_vat = Number(listVal);
      }
      const r = await axios.put(`${API}/products/${product.id}/quick-price`, body);
      if (r.data?.gomag?.pushed) {
        const listMsg = r.data.list_price_with_vat
          ? ` (întreg tăiat ${r.data.list_price_with_vat} RON)` : "";
        alert(`Preț actualizat: ${r.data.price_with_vat} RON${listMsg} · trimis la Gomag ✓`);
      } else if (r.data?.gomag?.error) {
        alert(`Preț actualizat local · push Gomag eșuat: ${r.data.gomag.error}`);
      }
      onSaved();
      setEditing(false);
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  if (editing) {
    const saleNet = saleVal && !isNaN(Number(saleVal)) ? (Number(saleVal) / 1.21).toFixed(2) : "—";
    const listNet = listVal && !isNaN(Number(listVal)) ? (Number(listVal) / 1.21).toFixed(2) : "—";
    return (
      <div className="flex flex-col items-end gap-1.5" data-testid={`quick-price-edit-${product.id}`}>
        <div className="flex items-center gap-1.5">
          <span className="text-[9px] uppercase text-zinc-500 w-14 text-right">Redus</span>
          <input autoFocus type="number" step="0.01" min="0" value={saleVal}
            onChange={(e) => setSaleVal(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
            data-testid={`quick-price-sale-${product.id}`}
            placeholder="cu TVA"
            className="w-24 rounded-md border border-orange-500/60 bg-zinc-900 px-2 py-1 text-right text-sm font-semibold text-zinc-100 outline-none" />
          <span className="text-[9px] text-zinc-500 w-16">/ {saleNet}<span className="text-[8px]"> fără</span></span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[9px] uppercase text-zinc-500 w-14 text-right">Întreg</span>
          <input type="number" step="0.01" min="0" value={listVal}
            onChange={(e) => setListVal(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
            data-testid={`quick-price-list-${product.id}`}
            placeholder="opțional"
            className="w-24 rounded-md border border-zinc-700 bg-zinc-900 px-2 py-1 text-right text-sm text-zinc-100 outline-none" />
          <span className="text-[9px] text-zinc-500 w-16">/ {listNet}<span className="text-[8px]"> fără</span></span>
        </div>
        <div className="flex gap-1">
          <button onClick={save} disabled={busy} title="Salvează + Push Gomag"
            data-testid={`quick-price-save-${product.id}`}
            className="inline-flex items-center gap-1 rounded-md border border-emerald-500/50 bg-emerald-500/15 px-2 py-1 text-[10px] font-semibold text-emerald-200 hover:bg-emerald-500/25 disabled:opacity-50">
            <Send className="h-3 w-3" /> {busy ? "..." : "Push Gomag"}
          </button>
          <button onClick={() => setEditing(false)} title="Renunță"
            className="rounded-md border border-zinc-800 p-1 text-zinc-400 hover:text-zinc-200">
            <X className="h-3 w-3" />
          </button>
        </div>
        <p className="text-[9px] text-zinc-500">Doar cu TVA · fără TVA se calc. automat (21%)</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-end">
      <div className="flex items-center gap-1.5">
        <p className="text-sm font-semibold text-zinc-100">{Number(priceNoVat || 0).toFixed(2)} <span className="text-[10px] text-zinc-500">RON</span></p>
        {isGomag && (
          <button onClick={() => setEditing(true)} title="Editează preț + push Gomag"
            data-testid={`quick-price-btn-${product.id}`}
            className="inline-flex items-center gap-1 rounded-md border border-sky-500/40 bg-sky-500/10 px-1.5 py-0.5 text-[10px] font-medium text-sky-300 hover:bg-sky-500/20 hover:text-sky-200">
            <Edit2 className="h-3 w-3" /> Edit
          </button>
        )}
      </div>
      {priceWithVat && priceWithVat !== priceNoVat && (
        <p className="text-xs text-zinc-500">{Number(priceWithVat).toFixed(2)} RON cu TVA</p>
      )}
      {listWithVat && Number(listWithVat) > Number(priceWithVat || 0) && (
        <p className="text-[10px] text-zinc-500 line-through">{Number(listWithVat).toFixed(2)} RON</p>
      )}
    </div>
  );
}

function StockReservationsBanner({ productId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/products/${productId}/reservations`).then((r) => {
      if (mounted) setData(r.data);
    }).catch(() => { if (mounted) setData(null); });
    return () => { mounted = false; };
  }, [productId]);
  if (!data) return null;
  const reserved = data.reserved || 0;
  const effective = data.effective_stock_to_gomag;
  const lastAt = data.last_pushed_at ? new Date(data.last_pushed_at).toLocaleString("ro-RO", { dateStyle: "short", timeStyle: "short" }) : "—";
  return (
    <div className="border-b border-zinc-800 bg-zinc-900/30 px-5 py-2.5 text-[11px]" data-testid="stock-reservations-banner">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
        <span className="text-zinc-500">Stoc Oblio: <span className="font-semibold text-zinc-100">{data.oblio_stock}</span></span>
        <span className="text-zinc-500">Rezervat (comenzi active): <span className={reserved > 0 ? "font-semibold text-amber-300" : "text-zinc-100"}>{reserved}</span></span>
        <span className="text-zinc-500">Trimis la Gomag: <span className="font-semibold text-emerald-300">{effective}</span></span>
        <span className="ml-auto text-zinc-600">Push: {lastAt}</span>
      </div>
      {reserved > 0 && data.reservations && data.reservations.length > 0 && (
        <details className="mt-1.5 text-zinc-500">
          <summary className="cursor-pointer hover:text-zinc-300">Vezi rezervări ({data.reservations.length})</summary>
          <ul className="mt-1 space-y-0.5 pl-3">
            {data.reservations.map((r, i) => (
              <li key={i} className="font-mono text-[10px]">
                {r.integration_key} · order #{r.order_id} · qty {r.qty} · status {r.status}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

const MARKETPLACE_FLAGS = {
  emag_ro_bau: "🇷🇴 RO BAU", emag_bg_bau: "🇧🇬 BG BAU", emag_hu_bau: "🇭🇺 HU BAU",
  emag_ro_fbe: "🇷🇴 RO FBE", emag_bg_fbe: "🇧🇬 BG FBE", emag_hu_fbe: "🇭🇺 HU FBE",
  trendyol_ro: "🇷🇴 Trendyol RO", trendyol_bg: "🇧🇬 Trendyol BG",
};

function LinkedOfferRow({ offer, busy, onUnlink, onQuickEdit, onToggleStatus, statusBusy }) {
  const isFbe = offer.integration_key?.endsWith("_fbe");
  const active = offer.status === 1;
  const ownership = offer.ownership;
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-zinc-800 bg-zinc-950/40 px-3 py-2"
      data-testid={`linked-offer-row-${offer.integration_key}-${offer.external_id}`}>
      {/* image */}
      {offer.main_image ? (
        <img src={offer.main_image} alt="" loading="lazy"
          className="h-12 w-12 flex-shrink-0 rounded-md border border-zinc-800 bg-zinc-900 object-contain" />
      ) : (
        <div className="h-12 w-12 flex-shrink-0 rounded-md border border-zinc-800 bg-zinc-900" />
      )}
      {/* identity + ids */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="rounded bg-zinc-800 px-1.5 py-0.5 text-[10px] font-bold text-zinc-300">
            {MARKETPLACE_FLAGS[offer.integration_key] || offer.integration_key}
          </span>
          <span className="rounded bg-emerald-500/20 px-1.5 py-0.5 text-[10px] font-bold text-emerald-200">Linkuit</span>
        </div>
        <p className="mt-0.5 truncate text-xs text-zinc-100" title={offer.name}>{offer.name}</p>
        <p className="mt-0.5 flex flex-wrap gap-x-2 text-[10px] text-zinc-500">
          <span>ID <span className="font-mono text-sky-300">{offer.external_id}</span></span>
          <span>SKU <span className="font-mono text-zinc-400">{offer.sku}</span></span>
          {offer.pnk && <span>PNK <span className="font-mono text-sky-300">{offer.pnk}</span></span>}
        </p>
      </div>
      {/* price + quick edit — display CU TVA (gross) as primary, fara TVA
          (netto = eMAG sale_price din backend) mic dedesubt. */}
      <div className="flex flex-shrink-0 flex-col items-end gap-0.5">
        <button onClick={() => onQuickEdit(offer)} disabled={busy}
          data-testid={`linked-quick-edit-${offer.integration_key}-${offer.external_id}`}
          title="Editare rapidă preț+stoc"
          className="inline-flex flex-col items-end gap-0 rounded px-1.5 py-0.5 text-right hover:bg-zinc-900/60 disabled:opacity-50">
          <span className="inline-flex items-center gap-1">
            <span className="text-xs font-semibold text-zinc-100">{Number((offer.price || 0) * (1 + (offer.vat_rate || 0))).toFixed(2)} {offer.currency}</span>
            <Edit2 className="h-2.5 w-2.5 text-zinc-500" />
          </span>
          <span className="text-[9px] text-zinc-500">fără TVA: {Number(offer.price || 0).toFixed(2)}</span>
        </button>
        <span className="text-[10px] text-zinc-500">Stoc: <span className={offer.stock > 0 ? "text-emerald-300" : "text-rose-300"}>{offer.stock}</span></span>
      </div>
      {/* badges: ownership + status + position */}
      <div className="flex flex-shrink-0 flex-col gap-1 text-[10px]">
        <span className={`rounded px-1.5 py-0.5 text-center font-semibold ${ownership ? "bg-emerald-500/15 text-emerald-200" : "bg-zinc-800 text-zinc-400"}`}
          title="Deținător documentație">
          Doc {ownership ? "✓" : "—"}
        </span>
        {offer.position != null && (
          <span className="rounded bg-sky-500/15 px-1.5 py-0.5 text-center font-semibold text-sky-200" title="Poziție Buy Button">
            Poz #{offer.position}
          </span>
        )}
      </div>
      {/* status toggle + actions */}
      <div className="flex flex-shrink-0 flex-col items-end gap-1">
        {/* activate/deactivate switch — only eMAG (Trendyol uses archived) */}
        {offer.integration_key?.startsWith("emag_") ? (
          <button onClick={() => onToggleStatus(offer)} disabled={statusBusy || busy}
            data-testid={`linked-status-toggle-${offer.integration_key}-${offer.external_id}`}
            title={active ? "Click pentru a dezactiva (scoate de la vânzare pe eMAG)" : "Click pentru a activa (pune la vânzare pe eMAG)"}
            className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[10px] font-semibold transition disabled:opacity-50 ${
              active
                ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-200 hover:bg-emerald-500/25"
                : "border-zinc-700 bg-zinc-900 text-zinc-400 hover:bg-zinc-800"
            }`}>
            <span className={`relative inline-block h-3.5 w-7 rounded-full transition ${active ? "bg-emerald-500/60" : "bg-zinc-700"}`}>
              <span className={`absolute top-0.5 h-2.5 w-2.5 rounded-full bg-white transition ${active ? "left-4" : "left-0.5"}`} />
            </span>
            {active ? "Activ" : "Inactiv"}
          </button>
        ) : (
          <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${active ? "bg-emerald-500/15 text-emerald-200" : "bg-zinc-800 text-zinc-400"}`}>
            {active ? "Activ" : "Inactiv"}
          </span>
        )}
        <div className="flex gap-1">
          {offer.product_url && (
            <a href={offer.product_url} target="_blank" rel="noopener noreferrer" title="Deschide pe marketplace"
              className="rounded border border-zinc-700 bg-zinc-900 p-1 text-emerald-300 hover:bg-emerald-500/10">
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
          <button onClick={() => onUnlink(offer)} disabled={busy}
            data-testid={`unlink-${offer.integration_key}-${offer.external_id}`}
            title="Dezleagă această ofertă"
            className="rounded border border-rose-500/40 bg-rose-500/10 p-1 text-rose-300 hover:bg-rose-500/20 disabled:opacity-50">
            <X className="h-3 w-3" />
          </button>
        </div>
      </div>
      {isFbe && (
        <span className="absolute right-1 top-1 hidden" data-testid={`fbe-marker-${offer.external_id}`}>fbe</span>
      )}
    </div>
  );
}

// Quick price+stock editor popover used inside the Products Modal (linked
// offers tab). Mirrors the marketplace-offers Quick Edit modal but kept
// inline to avoid an extra portal layer.
function LinkedOfferQuickEdit({ offer, onClose, onSaved }) {
  const isFbe = offer.integration_key?.endsWith("_fbe");
  const isTrendyol = offer.integration_key?.startsWith("trendyol_");
  const vat = offer.vat_rate ?? 0.21;
  // `offer.price` din backend = sale_price netto (fără TVA). Modalul cere „Preț
  // cu TVA", deci convertim la gross la deschidere; save-ul trimite tot pwv
  // (cu TVA) la backend care îl împarte înapoi pentru eMAG.
  const initialPwv = offer.price == null || offer.price === "" ? "" : +(Number(offer.price) * (1 + vat)).toFixed(2);
  const [pwv, setPwv] = useState(initialPwv);
  const [stock, setStock] = useState(offer.stock ?? 0);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  // Recommended price (afișat cu reducere) — eMAG sale_price = pwv (fără TVA derivat backend)
  const pnv = pwv === "" || pwv == null ? null : Math.round((Number(pwv) / (1 + vat)) * 100) / 100;

  const save = async () => {
    setSaving(true); setErr("");
    try {
      const body = { price_with_vat: Number(pwv) };
      if (!isFbe) body.stock = Math.max(0, parseInt(stock || 0, 10));
      const r = await axios.put(
        `${API}/marketplace-offers/${offer.integration_key}/${offer.external_id}/price`,
        body,
      );
      const pushed = r.data?.marketplace_push?.applied;
      // eslint-disable-next-line no-console
      console.log("[linked-offer-quick-edit] pushed=", pushed, r.data);
      onSaved && onSaved();
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4" onClick={onClose}
      data-testid="linked-offer-quick-edit">
      <div className="w-full max-w-sm rounded-xl border border-zinc-800 bg-zinc-950 p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h4 className="text-sm font-semibold text-zinc-100">
            Editare rapidă — {MARKETPLACE_FLAGS[offer.integration_key]}
          </h4>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-100"><X className="h-4 w-4" /></button>
        </div>
        <p className="mb-3 truncate text-[11px] text-zinc-500" title={offer.name}>{offer.name}</p>
        {err && <div className="mb-2 rounded border border-rose-500/40 bg-rose-500/10 px-2 py-1 text-[11px] text-rose-200">{err}</div>}
        <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">Preț cu TVA ({offer.currency})</label>
        <input type="number" step="0.01" value={pwv} onChange={(e) => setPwv(e.target.value)}
          data-testid="linked-quick-edit-price"
          className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-orange-500/50" />
        <p className="mt-0.5 text-[10px] text-zinc-500">Fără TVA: {pnv ?? "—"} {offer.currency}</p>
        <div className="mt-3">
          <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">{isFbe ? "Stoc (FBE — read-only)" : "Stoc"}</label>
          <input type="number" step="1" value={stock} onChange={(e) => setStock(e.target.value)}
            readOnly={isFbe} data-testid="linked-quick-edit-stock"
            className={`w-full rounded-lg border px-3 py-2 text-sm outline-none ${
              isFbe ? "border-zinc-800 bg-zinc-900/40 text-zinc-400" : "border-zinc-800 bg-zinc-900 text-zinc-100 focus:border-orange-500/50"
            }`} />
          {isFbe && <p className="mt-0.5 text-[10px] text-zinc-500">FBE: stocul e gestionat de eMAG.</p>}
        </div>
        {isTrendyol && <p className="mt-2 text-[10px] text-amber-300">Trendyol: doar preț+stoc; status nu se modifică prin API.</p>}
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300">Anulează</button>
          <button onClick={save} disabled={saving} data-testid="linked-quick-edit-save"
            className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-b from-orange-500 to-rose-500 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50">
            <Send className="h-3.5 w-3.5" /> {saving ? "..." : "Salvează + Push"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function LinkedOffersPanel({ productId }) {
  const [loading, setLoading] = useState(true);
  const [offers, setOffers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [error, setError] = useState("");
  const [quickEditOffer, setQuickEditOffer] = useState(null);

  const reload = async () => {
    setLoading(true); setError("");
    try {
      const r = await axios.get(`${API}/products/${productId}/linked-offers`);
      setOffers(r.data?.offers || []);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, [productId]);

  const unlink = async (offer) => {
    if (!window.confirm(`Dezleagă oferta ${offer.integration_key} #${offer.external_id}?`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/products/${productId}/link-offer/${offer.integration_key}/${offer.external_id}`);
      await reload();
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const toggleStatus = async (offer) => {
    const next = offer.status === 1 ? 0 : 1;
    const action = next === 1 ? "ACTIVEZI (pui la vânzare)" : "DEZACTIVEZI (scoți de la vânzare)";
    if (!window.confirm(`Sigur ${action} oferta pe ${offer.integration_key}?\n\nAcțiunea este trimisă imediat la eMAG.`)) return;
    setStatusBusy(true); setError("");
    try {
      const r = await axios.put(
        `${API}/marketplace-offers/${offer.integration_key}/${offer.external_id}`,
        { status: next },
      );
      const applied = r.data?.marketplace_push?.applied;
      if (!applied) {
        setError(`Schimbat local, dar push către eMAG a eșuat: ${r.data?.marketplace_push?.error || "verifică logs"}`);
      }
      await reload();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setStatusBusy(false); }
  };

  if (loading) return <p className="text-xs text-zinc-500">Încarc ofertele linkuite...</p>;

  return (
    <div className="space-y-4" data-testid="oferte-conectate-panel">
      {error && <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">{error}</div>}

      <section>
        <div className="mb-2 flex items-center justify-between">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-zinc-300">
            Oferte linkuite ({offers.length})
          </h4>
        </div>
        {offers.length === 0 ? (
          <div className="rounded-lg border border-dashed border-zinc-800 bg-zinc-950/40 px-4 py-3 text-center text-[11px] text-zinc-500" data-testid="linked-offers-empty">
            Nicio ofertă linkuită. Folosește pagina <span className="text-sky-300">Oferte Marketplace</span> pentru a face linkul.
          </div>
        ) : (
          <div className="space-y-2" data-testid="linked-offers-list">
            {offers.map((o) => (
              <LinkedOfferRow key={`${o.integration_key}-${o.external_id}`}
                offer={o} busy={busy} statusBusy={statusBusy}
                onUnlink={unlink}
                onToggleStatus={toggleStatus}
                onQuickEdit={(off) => setQuickEditOffer(off)} />
            ))}
          </div>
        )}
      </section>

      {quickEditOffer && (
        <LinkedOfferQuickEdit
          offer={quickEditOffer}
          onClose={() => setQuickEditOffer(null)}
          onSaved={() => reload()}
        />
      )}
    </div>
  );
}

// Reusable status banner that surfaces the result of the last Gomag push for
// the currently-saved set of fields. Tells the user whether the local change
// was successfully reflected on Gomag (best-effort — patch/json sometimes
// returns NO DATA even on success).
function GomagPushStatus({ status, onPush, pushing }) {
  if (!status) return (
    <div className="flex items-center justify-between gap-2 rounded-lg border border-zinc-800 bg-zinc-900/40 px-3 py-2 text-[11px] text-zinc-400">
      <span>Modificările sunt salvate local. Apasă <span className="text-zinc-200">Salvează + Push Gomag</span> pentru a le publica pe Gomag.</span>
    </div>
  );
  const applied = status.applied === true;
  const attempted = status.attempted === true;
  const err = status.error;
  return (
    <div className={`flex items-start justify-between gap-2 rounded-lg border px-3 py-2 text-[11px] ${
      applied ? "border-emerald-500/40 bg-emerald-500/5 text-emerald-200" :
      attempted ? "border-amber-500/40 bg-amber-500/5 text-amber-200" :
      "border-zinc-800 bg-zinc-900/40 text-zinc-400"
    }`}>
      <div className="flex-1">
        {applied ? <span>✓ Trimis cu succes la Gomag.</span> :
          attempted ? <span>⚠ Salvat local. Gomag a răspuns: <code className="font-mono text-[10px]">{err || "NO DATA"}</code>. API-ul Gomag are limitări pentru patch — verifică manual în Gomag dacă e nevoie.</span> :
          <span>Salvat doar local (push neîncercat).</span>}
      </div>
      {onPush && <button onClick={onPush} disabled={pushing}
        data-testid="gomag-retry-push"
        className="rounded border border-zinc-700 bg-zinc-900 px-2 py-1 text-[10px] text-zinc-300 hover:text-zinc-100 disabled:opacity-50">
        {pushing ? "..." : "Reîncearcă"}
      </button>}
    </div>
  );
}

// Editable HTML textarea with optional rendered preview toggle.
function HtmlEditor({ value, onChange, testId, placeholder }) {
  const [preview, setPreview] = useState(false);
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-end gap-1">
        <button type="button" onClick={() => setPreview(false)}
          className={`rounded-md px-2 py-0.5 text-[10px] ${!preview ? "bg-orange-500/20 text-orange-200" : "text-zinc-500 hover:text-zinc-300"}`}>
          HTML
        </button>
        <button type="button" onClick={() => setPreview(true)}
          className={`rounded-md px-2 py-0.5 text-[10px] ${preview ? "bg-orange-500/20 text-orange-200" : "text-zinc-500 hover:text-zinc-300"}`}>
          Preview
        </button>
      </div>
      {preview ? (
        <div className="min-h-32 max-h-96 overflow-y-auto rounded-xl border border-zinc-800 bg-zinc-900/40 p-4 text-sm leading-relaxed text-zinc-300"
          dangerouslySetInnerHTML={{ __html: value || "<em class='text-zinc-600'>Fără descriere</em>" }} />
      ) : (
        <textarea
          data-testid={testId}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={12}
          className="w-full rounded-xl border border-zinc-800 bg-zinc-900 px-3 py-2 font-mono text-xs text-zinc-100 outline-none focus:border-orange-500/50"
        />
      )}
    </div>
  );
}

// Category dropdown with search. Uses /api/gomag/categories endpoint.
function CategoryPicker({ selectedIds, onChange }) {
  const [allCats, setAllCats] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/gomag/categories`).then(r => setAllCats(r.data?.categories || [])).catch(() => setAllCats([]));
  }, []);

  const sel = new Set((selectedIds || []).map(String));
  const selectedCats = allCats.filter((c) => sel.has(c.id));
  const visible = search
    ? allCats.filter((c) => c.path.toLowerCase().includes(search.toLowerCase())).slice(0, 50)
    : allCats.slice(0, 50);

  const toggle = (id) => {
    const next = new Set(sel);
    if (next.has(String(id))) next.delete(String(id)); else next.add(String(id));
    onChange(Array.from(next));
  };

  return (
    <div className="relative">
      <div className="flex flex-wrap gap-1.5 rounded-lg border border-zinc-800 bg-zinc-900/40 p-2" data-testid="category-picker-selected">
        {selectedCats.length === 0 && <span className="text-[11px] text-zinc-500">Nicio categorie selectată</span>}
        {selectedCats.map((c) => (
          <span key={c.id} className="inline-flex items-center gap-1 rounded-md bg-orange-500/15 px-2 py-0.5 text-[11px] text-orange-200">
            {c.path}
            <button type="button" onClick={() => toggle(c.id)} className="hover:text-rose-300">
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <button type="button" onClick={() => setOpen(!open)}
          data-testid="category-picker-toggle"
          className="ml-auto inline-flex items-center gap-1 rounded-md border border-zinc-700 bg-zinc-900 px-2 py-0.5 text-[11px] text-zinc-300 hover:text-zinc-100">
          <Plus className="h-3 w-3" /> {open ? "Închide" : "Adaugă"}
        </button>
      </div>
      {open && (
        <div className="mt-2 rounded-lg border border-zinc-800 bg-zinc-950 p-2 shadow-xl" data-testid="category-picker-dropdown">
          <input type="text" autoFocus value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Caută categorie..."
            className="mb-2 w-full rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1 text-xs text-zinc-100 outline-none focus:border-orange-500/50" />
          <div className="max-h-60 overflow-y-auto">
            {visible.length === 0 ? (
              <p className="px-2 py-2 text-[11px] text-zinc-500">Nicio categorie</p>
            ) : visible.map((c) => (
              <button key={c.id} type="button" onClick={() => toggle(c.id)}
                className={`flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-xs hover:bg-zinc-900 ${
                  sel.has(c.id) ? "text-emerald-300" : "text-zinc-300"
                }`}>
                <span className="truncate" style={{ paddingLeft: `${(c.depth || 0) * 12}px` }}>{c.path}</span>
                {sel.has(c.id) && <CheckCircle2 className="h-3 w-3 flex-shrink-0" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Editable list of images. Supports add via URL, upload from disk (saved in
// MongoDB), reorder via drag (basic), remove.
function ImagesEditor({ productId, images, onChange }) {
  const [urlInput, setUrlInput] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const addUrl = () => {
    const u = urlInput.trim();
    if (!u) return;
    if (!/^https?:\/\//i.test(u)) {
      alert("URL invalid (trebuie să înceapă cu http(s)://)");
      return;
    }
    onChange([...(images || []), u]);
    setUrlInput("");
  };

  const remove = (i) => {
    const next = [...images];
    next.splice(i, 1);
    onChange(next);
  };

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= images.length) return;
    const next = [...images];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await axios.post(`${API}/products/${productId}/gomag-image/upload`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      onChange(r.data?.images || images);
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-3" data-testid="images-editor">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
        {(images || []).map((img, i) => (
          <div key={img + i} className="group relative aspect-square overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900"
            data-testid={`image-${i}`}>
            <img src={img.startsWith("/api/") ? `${BACKEND}${img}` : img} alt=""
              loading="lazy" className="h-full w-full object-contain" />
            <div className="absolute bottom-1 left-1 right-1 flex items-center justify-between gap-1 opacity-0 transition group-hover:opacity-100">
              <span className="rounded bg-black/70 px-1 py-0.5 text-[10px] font-mono text-zinc-300">{i + 1}</span>
              <div className="flex gap-0.5">
                {i > 0 && <button type="button" onClick={() => move(i, -1)}
                  className="rounded bg-black/70 px-1 py-0.5 text-[10px] text-zinc-300 hover:text-zinc-100">←</button>}
                {i < images.length - 1 && <button type="button" onClick={() => move(i, 1)}
                  className="rounded bg-black/70 px-1 py-0.5 text-[10px] text-zinc-300 hover:text-zinc-100">→</button>}
                <button type="button" onClick={() => remove(i)} data-testid={`image-delete-${i}`}
                  className="rounded bg-rose-500/80 px-1 py-0.5 text-[10px] text-white hover:bg-rose-500">
                  <Trash2 className="h-2.5 w-2.5" />
                </button>
              </div>
            </div>
          </div>
        ))}
        <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
          data-testid="image-upload-btn"
          className="flex aspect-square flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-zinc-800 bg-zinc-950/40 text-[11px] text-zinc-500 hover:border-orange-500/40 hover:text-orange-200 disabled:opacity-50">
          {uploading ? <RefreshCw className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
          <span>{uploading ? "Se încarcă..." : "Încarcă imagine"}</span>
        </button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} />
      </div>
      <div className="flex items-center gap-2">
        <input type="text" value={urlInput} onChange={(e) => setUrlInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addUrl()}
          placeholder="https://example.com/imagine.jpg"
          data-testid="image-url-input"
          className="flex-1 rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-orange-500/50" />
        <button type="button" onClick={addUrl} data-testid="image-url-add"
          className="inline-flex items-center gap-1 rounded-lg border border-sky-500/40 bg-sky-500/10 px-3 py-2 text-xs text-sky-200 hover:bg-sky-500/20">
          <Link2 className="h-3.5 w-3.5" /> Adaugă URL
        </button>
      </div>
    </div>
  );
}

// Editable list of attributes (Gomag characteristics).
function AttributesEditor({ attributes, onChange }) {
  const update = (i, field, val) => {
    const next = [...attributes];
    next[i] = { ...next[i], [field]: val };
    onChange(next);
  };
  const remove = (i) => {
    const next = [...attributes];
    next.splice(i, 1);
    onChange(next);
  };
  const add = () => onChange([...(attributes || []), { name: "", value: "" }]);

  return (
    <div className="space-y-2" data-testid="attributes-editor">
      <div className="overflow-hidden rounded-xl border border-zinc-800">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/60">
            <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-zinc-500">
              <th className="px-3 py-2 w-1/3">Caracteristică</th>
              <th className="px-3 py-2">Valoare</th>
              <th className="px-3 py-2 w-12"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800/80">
            {(attributes || []).map((attr, i) => (
              <tr key={i} data-testid={`attr-edit-row-${i}`}>
                <td className="px-2 py-1.5">
                  <input value={attr.name || ""} onChange={(e) => update(i, "name", e.target.value)}
                    data-testid={`attr-name-${i}`}
                    placeholder="Nume"
                    className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1 text-xs text-zinc-100 outline-none focus:border-orange-500/50" />
                </td>
                <td className="px-2 py-1.5">
                  <input value={attr.value || ""} onChange={(e) => update(i, "value", e.target.value)}
                    data-testid={`attr-value-${i}`}
                    placeholder="Valoare"
                    className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1 text-xs text-zinc-100 outline-none focus:border-orange-500/50" />
                </td>
                <td className="px-2 py-1.5">
                  <button type="button" onClick={() => remove(i)} data-testid={`attr-delete-${i}`}
                    className="rounded-md border border-rose-500/40 bg-rose-500/10 p-1 text-rose-300 hover:bg-rose-500/20">
                    <Trash2 className="h-3 w-3" />
                  </button>
                </td>
              </tr>
            ))}
            {(attributes || []).length === 0 && (
              <tr><td colSpan="3" className="px-3 py-3 text-center text-xs text-zinc-500">Nicio caracteristică. Apasă „Adaugă&rdquo; pentru prima.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <button type="button" onClick={add} data-testid="attr-add-btn"
        className="inline-flex items-center gap-1 rounded-md border border-zinc-700 bg-zinc-900 px-2 py-1 text-xs text-zinc-300 hover:text-zinc-100">
        <Plus className="h-3 w-3" /> Adaugă caracteristică
      </button>
    </div>
  );
}

// Editable FAQ list for the GEO tab.
function FaqEditor({ faq, onChange }) {
  const update = (i, field, val) => {
    const next = [...faq];
    next[i] = { ...next[i], [field]: val };
    onChange(next);
  };
  const remove = (i) => {
    const next = [...faq];
    next.splice(i, 1);
    onChange(next);
  };
  const add = () => onChange([...(faq || []), { question: "", answer: "" }]);

  return (
    <div className="space-y-2" data-testid="faq-editor">
      {(faq || []).map((item, i) => (
        <div key={i} className="rounded-lg border border-zinc-800 bg-zinc-900/30 p-3 space-y-2" data-testid={`faq-row-${i}`}>
          <div className="flex items-start gap-2">
            <div className="flex-1 space-y-2">
              <input value={item.question || ""} onChange={(e) => update(i, "question", e.target.value)}
                data-testid={`faq-question-${i}`}
                placeholder="Întrebare"
                className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-sm font-semibold text-zinc-100 outline-none focus:border-orange-500/50" />
              <textarea value={item.answer || ""} onChange={(e) => update(i, "answer", e.target.value)}
                data-testid={`faq-answer-${i}`}
                placeholder="Răspuns" rows={3}
                className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-xs text-zinc-300 outline-none focus:border-orange-500/50" />
            </div>
            <button type="button" onClick={() => remove(i)} data-testid={`faq-delete-${i}`}
              className="rounded-md border border-rose-500/40 bg-rose-500/10 p-1.5 text-rose-300 hover:bg-rose-500/20">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      ))}
      {(faq || []).length === 0 && <p className="text-xs text-zinc-500">Nicio întrebare în FAQ.</p>}
      <button type="button" onClick={add} data-testid="faq-add-btn"
        className="inline-flex items-center gap-1 rounded-md border border-zinc-700 bg-zinc-900 px-2 py-1 text-xs text-zinc-300 hover:text-zinc-100">
        <Plus className="h-3 w-3" /> Adaugă întrebare/răspuns
      </button>
    </div>
  );
}

function ProductEditModal({ product, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [activeTab, setActiveTab] = useState("informatii");
  const [fullProduct, setFullProduct] = useState(null);

  // Editable Gomag-side state, owned by this modal. Initialised from fullProduct.
  const [gForm, setGForm] = useState({
    name: "", description: "", short_description: "", brand: "", ean: "",
    images: [], videos: [], attributes: [], categories: [],
    geo_seo: "", geo_faq: [],
  });
  const [gomagBusy, setGomagBusy] = useState(false);
  const [pushStatus, setPushStatus] = useState(null);
  const [usdToRmb, setUsdToRmb] = useState(7.2);
  // Editable pricing state — Preț REDUS (sale) & Preț ÎNTREG (list/RRP).
  // Both stored as "cu TVA" (gross). Net values are derived on render.
  const [priceForm, setPriceForm] = useState({ sale_gross: "", list_gross: "" });
  const [priceBusy, setPriceBusy] = useState(false);
  const [priceMsg, setPriceMsg] = useState("");

  useEffect(() => {
    axios.get(`${API}/calculations/price-calc/settings`)
      .then((r) => { const v = +r.data?.settings?.usd_to_rmb; if (v > 0) setUsdToRmb(v); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (product) {
      const custom = { ...(product.custom || {}) };
      if ((custom.stoc_oblio == null || custom.stoc_oblio === "") && product.gomag_stock != null) {
        custom.stoc_oblio = String(product.gomag_stock);
      }
      setForm(custom);
      setActiveTab("informatii");
      setPushStatus(null);
      // List endpoint trims description/images; refetch detail for full data.
      axios.get(`${API}/products/${product.id}`).then(r => {
        setFullProduct(r.data);
        const fp = r.data || {};
        // Feb 2026 fix: cand modalul e deschis din tabul Shopify (fara
        // product.custom in stub), form-ul „Informatii" e gol pana la
        // refetch. Re-populam formularul cu custom din docul complet.
        if (fp.custom && typeof fp.custom === "object") {
          const fpCustom = { ...fp.custom };
          if ((fpCustom.stoc_oblio == null || fpCustom.stoc_oblio === "") && fp.gomag_stock != null) {
            fpCustom.stoc_oblio = String(fp.gomag_stock);
          }
          setForm(fpCustom);
        }
        const flatCats = [];
        const rawCats = fp.categories || [];
        if (Array.isArray(rawCats)) {
          rawCats.forEach(branch => {
            if (Array.isArray(branch)) {
              branch.forEach(c => { if (c && c.id) flatCats.push(String(c.id)); });
            } else if (branch && branch.id) {
              flatCats.push(String(branch.id));
            }
          });
        } else if (rawCats && typeof rawCats === "object") {
          Object.values(rawCats).forEach(branch => {
            if (Array.isArray(branch)) branch.forEach(c => { if (c && c.id) flatCats.push(String(c.id)); });
          });
        }
        setGForm({
          name: fp.name || "",
          description: fp.description || "",
          short_description: fp.short_description || "",
          brand: fp.brand || "",
          ean: typeof fp.ean === "string" ? fp.ean : (Array.isArray(fp.ean) ? fp.ean[0] || "" : ""),
          images: Array.isArray(fp.images) ? [...fp.images] : [],
          videos: Array.isArray(fp.videos) ? fp.videos.map(v => typeof v === "string" ? { url: v, type: "" } : v) : [],
          attributes: Array.isArray(fp.attributes) ? fp.attributes.map(a => ({ name: a.name || "", value: a.value || "" })) : [],
          categories: flatCats,
          geo_seo: fp.geo_seo || "",
          geo_faq: Array.isArray(fp.geo_faq) ? fp.geo_faq : [],
        });
        setPushStatus(fp._gomag_push_status || null);
        // Seed the editable price form from the fresh product doc. Both
        // fields default to "cu TVA" (gross) — the UI derives the net
        // side on the fly.
        setPriceForm({
          sale_gross: fp.pricing?.price_with_vat != null
            ? String(fp.pricing.price_with_vat)
            : (fp.recommended_price != null ? String(fp.recommended_price) : ""),
          list_gross: fp.pricing?.list_price_with_vat != null
            ? String(fp.pricing.list_price_with_vat) : "",
        });
        setPriceMsg("");
      }).catch(() => setFullProduct(null));
    }
  }, [product]);

  if (!product) return null;
  const p = fullProduct || product;

  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));
  const setG = (k, v) => setGForm((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true); setError("");
    const payload = {};
    for (const [k, v] of Object.entries(form)) {
      if (v === "" || v == null) continue;
      const num = Number(v);
      payload[k] = isNaN(num) || typeof v === "string" && /[a-zA-Z×x,\s]/.test(v) ? v : num;
    }
    try {
      await axios.put(`${API}/products/${product.id}/custom`, payload);
      onSaved();
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  // Save and push a tab-specific subset of gForm to backend (+ Gomag).
  const saveGomagFields = async (keys, doPush = true) => {
    setGomagBusy(true); setError("");
    const payload = {};
    keys.forEach((k) => {
      if (k === "categories") {
        payload[k] = (gForm.categories || []).map(id => ({ id }));
      } else {
        payload[k] = gForm[k];
      }
    });
    try {
      const r = await axios.put(`${API}/products/${product.id}/gomag-fields?push=${doPush ? "true" : "false"}`, payload);
      setPushStatus(r.data?.gomag_push || null);
      // Refresh full product so subsequent edits start from latest persisted state.
      const fp = await axios.get(`${API}/products/${product.id}`);
      setFullProduct(fp.data);
      onSaved();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setGomagBusy(false); }
  };

  const retryPush = async () => {
    setGomagBusy(true);
    try {
      const r = await axios.post(`${API}/products/${product.id}/gomag-push`);
      setPushStatus(r.data?.gomag_push || null);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setGomagBusy(false); }
  };

  // Save Preț Redus + Preț Întreg (both gross) to backend, which pushes
  // both to Gomag in the same call: `specialPrice` = sale (gros), `price`
  // (which Gomag maps to `base_price`) = list (întreg, strikethrough).
  const savePricesAndPush = async () => {
    const sale = Number(priceForm.sale_gross);
    if (!sale || sale <= 0) {
      setPriceMsg("Introdu Prețul Redus (cu TVA) — este obligatoriu.");
      return;
    }
    const list = Number(priceForm.list_gross);
    const body = { price_with_vat: sale };
    if (list && list > 0) body.list_price_with_vat = list;
    setPriceBusy(true); setPriceMsg("");
    try {
      const r = await axios.put(`${API}/products/${product.id}/quick-price`, body);
      const pushed = r.data?.gomag?.pushed;
      if (pushed) {
        setPriceMsg(`✓ Salvat local · Gomag: preț redus ${r.data.price_with_vat} RON`
          + (r.data.list_price_with_vat ? ` · preț întreg ${r.data.list_price_with_vat} RON` : ""));
      } else if (r.data?.gomag?.error) {
        setPriceMsg(`⚠ Salvat local, push Gomag eșuat: ${r.data.gomag.error}`);
      } else {
        setPriceMsg("✓ Salvat local (Gomag inactiv).");
      }
      // Refresh full product so the "Preț" column in the list refetches
      const fp = await axios.get(`${API}/products/${product.id}`);
      setFullProduct(fp.data);
      onSaved();
    } catch (e) {
      setPriceMsg(`✗ ${e.response?.data?.detail || e.message}`);
    } finally { setPriceBusy(false); }
  };

  const refreshFromEmag = async () => {
    setRefreshing(true); setError("");
    try {
      const r = await axios.post(`${API}/products/${product.id}/refresh-emag`);
      const derived = r.data.derived || {};
      const fbe = derived.fbe_stock_emag;
      const ean = derived.ean_emag;
      const pnk = derived.pnk_emag;
      const commission = derived.emag_commission;
      setForm((f) => ({
        ...f,
        stoc_fbe: fbe != null ? String(fbe) : f.stoc_fbe,
        ean: ean ? String(ean) : f.ean,
        pnk: pnk ? String(pnk) : f.pnk,
        comision_emag: commission != null ? String(commission) : f.comision_emag,
      }));
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setRefreshing(false); }
  };

  const toggleArchive = async () => {
    const target = !product.is_archived;
    if (!window.confirm(target ? "Arhivezi acest produs? Va dispărea din lista activă." : "Reactivezi produsul?")) return;
    setArchiving(true); setError("");
    try {
      await axios.put(`${API}/products/${product.id}/archive`, { archived: target });
      onSaved();
      onClose();
    } catch (e) {
      // detail can be a string OR a FastAPI validation array/object — stringify
      // so React doesn't crash with "Objects are not valid as a React child".
      const raw = e?.response?.data?.detail || e?.message || "Eroare necunoscută";
      setError(typeof raw === "string" ? raw : JSON.stringify(raw));
    } finally { setArchiving(false); }
  };

  const hasCustomData = (() => {
    // Prefer full doc daca a fost deja incarcat (Feb 2026 fix: cand deschidem
    // din tabul Shopify, `product.custom` e {} iar `fullProduct.custom` are
    // datele reale).
    const c = (fullProduct && fullProduct.custom) || product.custom || {};
    return Object.keys(c).some((k) => !k.startsWith("_") && c[k] != null && c[k] !== "");
  })();

  const sections = [
    { title: "Stocuri", fields: [
      { k: "stoc_oblio", l: "Stoc Oblio", unit: "buc" },
      { k: "stoc_fbe", l: "Stoc FBE", unit: "buc" },
    ]},
    { title: "Identificare", fields: [
      { k: "sku_override", l: "SKU", unit: "" },
      { k: "pnk", l: "PNK", unit: "" },
      { k: "ean", l: "EAN", unit: "" },
      { k: "hs_code", l: "HS Code", unit: "" },
      { k: "taxa_vamala", l: "Taxă vamală", unit: "%" },
    ]},
    { title: "Comisioane", fields: [
      { k: "comision_emag", l: "Comision eMAG", unit: "%" },
      { k: "comision_fbe", l: "Comision FBE", unit: "RON" },
    ]},
    { title: "Costuri", fields: [
      { k: "pret_achizitie", l: "Preț achiziție", unit: "RON" },
      { k: "pret_usd_china", l: "Preț USD China", unit: "USD" },
      { k: "alte_costuri", l: "Alte costuri (reciclare)", unit: "RON" },
      { k: "cost_ads_procent", l: "Cost ads", unit: "%" },
    ]},
    { title: "Preț furnizor (RMB)", fields: [
      { k: "pret_produs_rmb", l: "Preț produs", unit: "RMB" },
      { k: "pret_cutie_rmb", l: "Preț cutie", unit: "RMB" },
      { k: "comision_china", l: "Comision China", unit: "%" },
      { k: "pret_manual_rmb", l: "Preț manual", unit: "RMB" },
    ]},
    { title: "Greutăți produs", fields: [
      { k: "greutate_carton", l: "Greutate carton produs", unit: "g" },
      { k: "greutate_plastic", l: "Greutate plastic produs", unit: "g" },
      { k: "greutate_cutie_transport", l: "Greutate cutie mare transport", unit: "g" },
      { k: "greutate_baterie", l: "Greutate baterie", unit: "g" },
    ]},
    { title: "Bax (ambalare în vrac)", fields: [
      { k: "bucati_per_bax", l: "Bucăți per bax", unit: "buc" },
      { k: "dimensiuni_bax", l: "Dimensiuni bax", unit: "cm (LxlxH)" },
      { k: "greutate_bax", l: "Greutate bax", unit: "kg" },
    ]},
    { title: "Prețuri manuale (override)", fields: [
      { k: "pret_cu_tva_manual", l: "Preț cu TVA", unit: "RON" },
      { k: "pret_fara_tva_manual", l: "Preț fără TVA", unit: "RON" },
    ]},
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 p-0 backdrop-blur-sm sm:items-center sm:p-4" onClick={onClose}>
      <div className="max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-t-2xl border border-zinc-800 bg-zinc-950 shadow-2xl sm:rounded-2xl" onClick={(e) => e.stopPropagation()} data-testid="product-edit-modal">
        <div className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-zinc-800 bg-zinc-950/95 px-5 py-3 backdrop-blur">
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-wider text-zinc-500">Editează produs</p>
            <h3 className="text-lg font-semibold text-zinc-100 break-words">{p.name}</h3>
            <p className="text-xs text-zinc-500">SKU: <span className="font-mono">{p.part_number}</span> · ID #{p.id}</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-zinc-400 hover:bg-zinc-800"><X className="h-4 w-4" /></button>
        </div>

        {/* TAB STRIP */}
        <div className="sticky top-[68px] z-10 flex gap-0 border-b border-zinc-800 bg-zinc-950/95 px-5 backdrop-blur overflow-x-auto" data-testid="product-modal-tabs">
          {[
            { id: "informatii", label: "Informații" },
            { id: "general", label: "General" },
            { id: "detalii", label: "Detalii" },
            { id: "caracteristici", label: "Caracteristici" },
            { id: "geo", label: "GEO" },
            { id: "oferte_conectate", label: "Oferte Conectate" },
          ].map((t) => (
            <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setActiveTab(t.id)}
              className={`relative -mb-px whitespace-nowrap px-4 py-2.5 text-xs font-semibold uppercase tracking-wider transition ${
                activeTab === t.id ? "text-orange-300" : "text-zinc-500 hover:text-zinc-300"
              }`}>
              {t.label}
              {activeTab === t.id && <span className="absolute inset-x-0 bottom-0 h-0.5 bg-orange-400" />}
            </button>
          ))}
        </div>

        {/* TAB 1 — Informații (existing fields) */}
        {activeTab === "informatii" && (<>
        <div className="border-b border-zinc-800 bg-zinc-900/40 px-5 py-2.5 flex flex-wrap items-center gap-2">
          <button data-testid="product-refresh-emag" onClick={refreshFromEmag} disabled={refreshing}
            className="inline-flex items-center gap-1.5 rounded-md border border-sky-500/40 bg-sky-500/10 px-3 py-1.5 text-xs text-sky-200 hover:bg-sky-500/20 disabled:opacity-50">
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Se preia din eMAG..." : "Auto-completează din eMAG (stoc FBE, EAN, PNK, comision)"}
          </button>
          <button data-testid="product-archive-toggle" onClick={toggleArchive} disabled={archiving}
            className={`ml-auto inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs disabled:opacity-50 ${product.is_archived ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20" : "border-zinc-700 bg-zinc-900 text-zinc-300 hover:text-zinc-100"}`}>
            {product.is_archived ? "Reactivează produsul" : "Arhivează produsul"}
          </button>
        </div>

        <StockReservationsBanner productId={product.id} />

        <div className="space-y-5 px-5 py-4">
          {error && <div className="flex items-start gap-2 rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs text-rose-200"><AlertCircle className="mt-0.5 h-3.5 w-3.5" />{error}</div>}

          {!hasCustomData && (
            <div className="flex items-start gap-2 rounded-lg border border-zinc-800 bg-zinc-900/40 px-3 py-2.5 text-[11px] text-zinc-400" data-testid="no-custom-data-hint">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-amber-400" />
              <div>
                <p className="text-zinc-300">Nu există date custom istorice pentru acest produs (preț achiziție, comisioane, dimensiuni etc.).</p>
                <p className="mt-0.5">Completează câmpurile de mai jos pentru a le salva. Datele vor rămâne legate de SKU-ul produsului inclusiv după resync din Gomag.</p>
              </div>
            </div>
          )}

          {sections.map((sec) => (
            <section key={sec.title} className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4">
              <h4 className="mb-3 text-xs font-semibold uppercase tracking-wider text-zinc-400">{sec.title}</h4>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {sec.fields.map((f) => (
                  <div key={f.k}>
                    <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">{f.l}</label>
                    <div className="flex items-center gap-1.5">
                      <input
                        data-testid={`field-${f.k}`}
                        type="text"
                        inputMode={["sku_override", "pnk", "ean", "hs_code", "dimensiuni_bax"].includes(f.k) ? "text" : "decimal"}
                        value={form[f.k] ?? ""}
                        onChange={(e) => setField(f.k, e.target.value)}
                        className="flex-1 rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-orange-500/50"
                      />
                      {f.unit && <span className="text-[10px] text-zinc-500 w-14">{f.unit}</span>}
                    </div>
                  </div>
                ))}
              </div>
              {sec.title === "Preț furnizor (RMB)" && (() => {
                const pp = parseFloat(String(form.pret_produs_rmb || "").replace(",", ".")) || 0;
                const pc = parseFloat(String(form.pret_cutie_rmb || "").replace(",", ".")) || 0;
                const cc = parseFloat(String(form.comision_china || "").replace(",", ".")) || 0;
                const total = pp + pc;
                const finalRmb = total * (1 + cc / 100);
                const priceUsd = usdToRmb > 0 ? (finalRmb / usdToRmb) : 0;
                if (total <= 0) return (
                  <div className="mt-3 rounded-md border border-zinc-800 bg-zinc-950/60 px-3 py-2 text-[11px] text-zinc-500" data-testid="china-usd-hint">
                    Completează prețul produs + preț cutie pentru a calcula automat Prețul USD China.
                  </div>
                );
                return (
                  <div className="mt-3 rounded-md border border-emerald-500/30 bg-emerald-500/[0.06] px-3 py-2 text-[11px] text-zinc-300" data-testid="china-usd-preview">
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                      <span>Total RMB: <span className="font-mono text-zinc-100">{total.toFixed(2)}</span></span>
                      <span>× (1 + {cc}%) = <span className="font-mono text-zinc-100">{finalRmb.toFixed(2)} RMB</span></span>
                      <span>÷ curs <span className="font-mono text-zinc-100">{usdToRmb.toFixed(3)}</span> RMB/USD</span>
                      <span className="ml-auto text-[12px] font-semibold text-emerald-300" data-testid="china-usd-computed">
                        = ${priceUsd.toFixed(2)} USD
                      </span>
                    </div>
                    <p className="mt-1 text-[10px] text-zinc-500">
                      Această valoare se folosește automat la „Adaugă produs existent&rdquo; în Calcul Preț Produse (are prioritate față de câmpul „Preț USD China&rdquo; completat manual).
                    </p>
                  </div>
                );
              })()}
            </section>
          ))}

          <ProductDocuments product={p} />
        </div>
        </>)}

        {/* TAB 2 — General (editable) */}
        {activeTab === "general" && (
          <div className="space-y-4 px-5 py-4" data-testid="tab-content-general">
            <GomagPushStatus status={pushStatus} onPush={retryPush} pushing={gomagBusy} />
            {error && <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">{error}</div>}
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">Nume produs</label>
                <input value={gForm.name} onChange={(e) => setG("name", e.target.value)}
                  data-testid="general-name-input"
                  className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-orange-500/50" />
              </div>
              <div>
                <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">SKU</label>
                <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 px-3 py-2 text-sm font-mono text-zinc-400">{p.part_number}</div>
              </div>
              <div>
                <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">Brand</label>
                <input value={gForm.brand} onChange={(e) => setG("brand", e.target.value)}
                  data-testid="general-brand-input"
                  className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-orange-500/50" />
              </div>
              <div>
                <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">EAN</label>
                <input value={gForm.ean} onChange={(e) => setG("ean", e.target.value)}
                  data-testid="general-ean-input"
                  className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm font-mono text-zinc-100 outline-none focus:border-orange-500/50" />
              </div>
              <ReadField label="Preț redus (fără TVA)"
                value={priceForm.sale_gross && !isNaN(Number(priceForm.sale_gross))
                  ? `${(Number(priceForm.sale_gross) / 1.21).toFixed(2)} RON` : "—"}
                testId="general-price-no-vat" />
              <div>
                <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">
                  Preț redus (cu TVA) <span className="text-orange-400">*</span>
                </label>
                <div className="relative">
                  <input type="number" step="0.01" min="0"
                    value={priceForm.sale_gross}
                    onChange={(e) => setPriceForm((f) => ({ ...f, sale_gross: e.target.value }))}
                    data-testid="general-price-with-vat-input"
                    placeholder="ex: 174.99"
                    className="w-full rounded-lg border border-orange-500/40 bg-zinc-900 px-3 py-2 pr-12 text-sm font-semibold text-zinc-100 outline-none focus:border-orange-500" />
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-zinc-500">RON</span>
                </div>
              </div>
              <ReadField label="Preț întreg (fără TVA)"
                value={priceForm.list_gross && !isNaN(Number(priceForm.list_gross))
                  ? `${(Number(priceForm.list_gross) / 1.21).toFixed(2)} RON` : "—"}
                testId="general-list-no-vat" />
              <div>
                <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">
                  Preț întreg (cu TVA) <span className="text-zinc-600">— opțional, pentru discount</span>
                </label>
                <div className="relative">
                  <input type="number" step="0.01" min="0"
                    value={priceForm.list_gross}
                    onChange={(e) => setPriceForm((f) => ({ ...f, list_gross: e.target.value }))}
                    data-testid="general-list-with-vat-input"
                    placeholder="ex: 234.99 (taiat cu linie pe Gomag)"
                    className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 pr-12 text-sm text-zinc-100 outline-none focus:border-orange-500/50" />
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-zinc-500">RON</span>
                </div>
              </div>
              <div className="sm:col-span-2 -mt-1 flex flex-wrap items-center justify-between gap-3">
                <p className="text-[11px] text-zinc-500">
                  Prețul redus e cel pe care îl plătește clientul. Prețul întreg apare tăiat cu o linie (discount).
                  Prețurile fără TVA se calculează automat (TVA 21%).
                </p>
                <button onClick={savePricesAndPush} disabled={priceBusy}
                  data-testid="general-save-prices-btn"
                  className="inline-flex items-center gap-2 rounded-lg border border-emerald-500/50 bg-emerald-500/15 px-3 py-1.5 text-xs font-semibold text-emerald-200 hover:bg-emerald-500/25 disabled:opacity-50">
                  <Send className="h-3.5 w-3.5" /> {priceBusy ? "Se trimite..." : "Salvează prețuri + Push Gomag"}
                </button>
              </div>
              {priceMsg && (
                <div className="sm:col-span-2 rounded-md border border-zinc-700 bg-zinc-900/50 px-3 py-2 text-[11px] text-zinc-300" data-testid="general-price-msg">
                  {priceMsg}
                </div>
              )}
            </div>
            <p className="text-[11px] text-zinc-500">Poți edita prețurile fie de aici (General), fie din lista de produse (Quick Edit). Ambele fac push imediat la Gomag.</p>
            <div>
              <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">Categorii Gomag</label>
              <CategoryPicker selectedIds={gForm.categories} onChange={(ids) => setG("categories", ids)} />
            </div>
            <div className="flex justify-end gap-2 border-t border-zinc-800 pt-3">
              <button onClick={() => saveGomagFields(["name", "brand", "ean", "categories"])} disabled={gomagBusy}
                data-testid="general-save-btn"
                className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-b from-orange-500 to-rose-500 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
                <Send className="h-4 w-4" /> {gomagBusy ? "Se publică..." : "Salvează + Push Gomag"}
              </button>
            </div>
          </div>
        )}

        {/* TAB 3 — Detalii (Descriere + Imagini + Video) editable */}
        {activeTab === "detalii" && (
          <div className="space-y-5 px-5 py-4" data-testid="tab-content-detalii">
            <GomagPushStatus status={pushStatus} onPush={retryPush} pushing={gomagBusy} />
            {error && <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">{error}</div>}

            <section>
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-400">Descriere completă (HTML)</h4>
              <HtmlEditor value={gForm.description} onChange={(v) => setG("description", v)} testId="detalii-description-editor"
                placeholder="<p>Descrierea produsului în HTML...</p>" />
            </section>

            <section>
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-400">Descriere scurtă</h4>
              <textarea value={gForm.short_description} onChange={(e) => setG("short_description", e.target.value)}
                data-testid="detalii-short-description"
                rows={2} placeholder="Descriere scurtă pentru previzualizări"
                className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-orange-500/50" />
            </section>

            <section>
              <h4 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-zinc-400">
                <ImageIcon className="h-3 w-3" /> Imagini ({(gForm.images || []).length})
              </h4>
              <ImagesEditor productId={product.id} images={gForm.images} onChange={(imgs) => setG("images", imgs)} />
            </section>

            <section>
              <h4 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-zinc-400">
                <VideoIcon className="h-3 w-3" /> Videoclipuri ({(gForm.videos || []).length})
              </h4>
              {(gForm.videos || []).map((v, i) => (
                <div key={i} className="mb-2 flex items-center gap-2" data-testid={`video-row-${i}`}>
                  <input value={v.url || ""} onChange={(e) => {
                    const next = [...gForm.videos]; next[i] = { ...next[i], url: e.target.value }; setG("videos", next);
                  }} placeholder="https://youtube.com/watch?v=..."
                    data-testid={`video-url-${i}`}
                    className="flex-1 rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-orange-500/50" />
                  <button onClick={() => {
                    const next = [...gForm.videos]; next.splice(i, 1); setG("videos", next);
                  }} data-testid={`video-delete-${i}`}
                    className="rounded-md border border-rose-500/40 bg-rose-500/10 p-2 text-rose-300 hover:bg-rose-500/20">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              <button onClick={() => setG("videos", [...(gForm.videos || []), { url: "", type: "" }])}
                data-testid="video-add-btn"
                className="inline-flex items-center gap-1 rounded-md border border-zinc-700 bg-zinc-900 px-2 py-1 text-xs text-zinc-300 hover:text-zinc-100">
                <Plus className="h-3 w-3" /> Adaugă video
              </button>
            </section>

            <div className="flex justify-end gap-2 border-t border-zinc-800 pt-3">
              <button onClick={() => saveGomagFields(["description", "short_description", "images", "videos"])} disabled={gomagBusy}
                data-testid="detalii-save-btn"
                className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-b from-orange-500 to-rose-500 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
                <Send className="h-4 w-4" /> {gomagBusy ? "Se publică..." : "Salvează + Push Gomag"}
              </button>
            </div>
          </div>
        )}

        {/* TAB 4 — Caracteristici (editable Gomag attributes) */}
        {activeTab === "caracteristici" && (
          <div className="space-y-4 px-5 py-4" data-testid="tab-content-caracteristici">
            <GomagPushStatus status={pushStatus} onPush={retryPush} pushing={gomagBusy} />
            {error && <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">{error}</div>}
            <AttributesEditor attributes={gForm.attributes} onChange={(a) => setG("attributes", a)} />
            <div className="flex justify-end gap-2 border-t border-zinc-800 pt-3">
              <button onClick={() => saveGomagFields(["attributes"])} disabled={gomagBusy}
                data-testid="caracteristici-save-btn"
                className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-b from-orange-500 to-rose-500 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
                <Send className="h-4 w-4" /> {gomagBusy ? "Se publică..." : "Salvează + Push Gomag"}
              </button>
            </div>
          </div>
        )}

        {/* TAB 5 — GEO (Descriere scurtă SEO + FAQ pentru roboții AI) */}
        {activeTab === "geo" && (
          <div className="space-y-5 px-5 py-4" data-testid="tab-content-geo">
            <div className="flex items-start gap-2 rounded-lg border border-sky-500/30 bg-sky-500/5 px-3 py-2 text-[11px] text-sky-200">
              <MapPin className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Secțiunea GEO pentru roboții AI</p>
                <p className="text-sky-300/70">Descriere Produs Scurt + FAQ. Stocate local — vor fi sincronizate cu Gomag pe măsură ce platforma expune API-ul.</p>
              </div>
            </div>
            {error && <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">{error}</div>}

            <section>
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-400">Descriere Produs Scurt</h4>
              <p className="mb-1.5 text-[10px] text-zinc-500">Rezumat cu informațiile necesare pentru ca roboții AI să înțeleagă mai bine produsul.</p>
              <textarea value={gForm.geo_seo} onChange={(e) => setG("geo_seo", e.target.value)}
                data-testid="geo-seo-input" rows={5}
                placeholder="Ex: Aparat de calcat vertical cu aburi, putere 1200W, portabil, pliabil, rezervor 200ml..."
                className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-orange-500/50" />
            </section>

            <section>
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-400">FAQ (Întrebări frecvente)</h4>
              <p className="mb-2 text-[10px] text-zinc-500">Întrebări și răspunsuri pentru a ajuta roboții AI să înțeleagă produsul.</p>
              <FaqEditor faq={gForm.geo_faq} onChange={(f) => setG("geo_faq", f)} />
            </section>

            <div className="flex justify-end gap-2 border-t border-zinc-800 pt-3">
              <button onClick={() => saveGomagFields(["geo_seo", "geo_faq"], false)} disabled={gomagBusy}
                data-testid="geo-save-btn"
                className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-b from-orange-500 to-rose-500 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
                <Save className="h-4 w-4" /> {gomagBusy ? "Se salvează..." : "Salvează local"}
              </button>
            </div>
          </div>
        )}

        {/* TAB 6 — Oferte Conectate (linked marketplace offers) */}
        {activeTab === "oferte_conectate" && (
          <div className="space-y-3 px-5 py-4" data-testid="tab-content-oferte-conectate">
            <p className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-200">
              Ofertele din Marketplace Oferte conectate cu acest produs Gomag. Linkurile vor putea fi gestionate din pagina Oferte Marketplace — în curs.
            </p>
            <LinkedOffersPanel productId={product.id} />
          </div>
        )}

        <div className="sticky bottom-0 flex gap-2 border-t border-zinc-800 bg-zinc-950 px-5 py-3">
          <button onClick={onClose} className="flex-1 rounded-lg border border-zinc-800 bg-zinc-900 py-2 text-sm text-zinc-300">Închide</button>
          {activeTab === "informatii" && (
            <button onClick={save} disabled={saving} data-testid="product-save-btn"
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-b from-orange-500 to-rose-500 py-2 text-sm font-medium text-white disabled:opacity-50">
              <Save className="h-4 w-4" /> {saving ? "Se salvează..." : "Salvează"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Products() {
  const [platformTab, setPlatformTab] = useState("gomag");  // "gomag" | "shopify"
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [search, setSearch] = useState("");
  const [skuSearch, setSkuSearch] = useState("");
  const [pnkSearch, setPnkSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState(""); // "", "1", "0"
  const [errorsFilter, setErrorsFilter] = useState(""); // "", "with", "without"
  const [availabilityFilter, setAvailabilityFilter] = useState(""); // "", "in_stock", "low_stock", "out_of_stock"
  // Filter mode for archived products (user request 2026-02-05):
  // "active" (default), "only" (only archived), "all" (both).
  const [archivedMode, setArchivedMode] = useState("active");
  // Sortare pe click header — click 1 asc, click 2 desc, click 3 reset.
  // { key: "name"|"price"|"units_sold"|"status", dir: "asc"|"desc" } | null
  const [sortBy, setSortBy] = useState(null);
  const cycleSort = (key) => setSortBy((prev) => {
    if (!prev || prev.key !== key) return { key, dir: "asc" };
    if (prev.dir === "asc") return { key, dir: "desc" };
    return null; // 3rd click = reset
  });
  const [editProduct, setEditProduct] = useState(null);
  const [expandedOffersId, setExpandedOffersId] = useState(null);
  const [pushingStock, setPushingStock] = useState(false);
  const [pullingOblio, setPullingOblio] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const params = { source: "gomag" };
      // 2026-02 fix: search/sku/pnk sunt filtre client-side (via useMemo mai jos)
      // ca sa evitam race condition intre request-urile lansate la fiecare tasta.
      // Aceleasi reguli ca pagina Oferte Marketplace.
      if (activeFilter !== "") params.status = Number(activeFilter);
      if (errorsFilter === "with") params.has_errors = "true";
      else if (errorsFilter === "without") params.has_errors = "false";
      if (availabilityFilter) params.availability = availabilityFilter;
      // 3-state archived filter (user request 2026-02-05):
      //   "active" → show only non-archived (default), server sends show_archived=false
      //   "only"   → only archived → server flag archived_only=true
      //   "all"    → both → show_archived=true
      if (archivedMode === "only") {
        params.archived_only = "true";
      } else if (archivedMode === "all") {
        params.show_archived = "true";
      } else {
        params.show_archived = "false";
      }
      const p = await axios.get(`${API}/products`, { params });
      setProducts(p.data.products || []);
      setStats({
        total: p.data.count || 0,
        active: (p.data.products || []).filter(x => x.status === 1).length,
        out_of_stock: (p.data.products || []).filter(x => stockTotal(x) <= 0).length,
      });
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  }, [activeFilter, errorsFilter, availabilityFilter, archivedMode]);

  // Client-side filter pe search / SKU / PNK — instant, fara race conditions.
  // Post-filter aplica si sortarea pe coloana selectata (click header).
  const filteredProducts = useMemo(() => {
    const q = search.trim().toLowerCase();
    const qSku = skuSearch.trim().toLowerCase();
    const qPnk = pnkSearch.trim().toLowerCase();
    const base = (!q && !qSku && !qPnk) ? products : products.filter((p) => {
      if (q) {
        const name = String(p.name || "").toLowerCase();
        // Nume search: match si pe SKU/PNK/EAN pentru convenienta
        const alt = [p.sku, p.pnk, p.ean, p.gomag_id, p._emag_id]
          .filter(Boolean).map(x => String(x).toLowerCase());
        if (!name.includes(q) && !alt.some(a => a.includes(q))) return false;
      }
      if (qSku && !String(p.sku || "").toLowerCase().includes(qSku)) return false;
      if (qPnk) {
        const pnks = [p.pnk, ...(p.marketplace_pnks || [])]
          .filter(Boolean).map(x => String(x).toLowerCase());
        if (!pnks.some(a => a.includes(qPnk))) return false;
      }
      return true;
    });
    if (!sortBy) return base;
    const getVal = (p) => {
      switch (sortBy.key) {
        case "name": return String(p.name || "").toLowerCase();
        case "price": return Number(p.price_no_vat ?? p.sale_price ?? 0);
        case "units_sold": return Number(p.units_sold_30d || 0);
        case "status": return Number(p.status || 0);
        default: return 0;
      }
    };
    const sorted = [...base].sort((a, b) => {
      const va = getVal(a); const vb = getVal(b);
      if (va < vb) return sortBy.dir === "asc" ? -1 : 1;
      if (va > vb) return sortBy.dir === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [products, search, skuSearch, pnkSearch, sortBy]);

  // Refresh trigger from descendant toggles (status cascade).
  useEffect(() => {
    const h = () => load();
    window.addEventListener('products-refresh', h);
    return () => window.removeEventListener('products-refresh', h);
  }, [load]);

  const handleSync = async () => {
    setSyncing(true); setError(""); setSuccess("");
    try {
      const r = await axios.post(`${API}/products/sync-from-gomag`);
      setSuccess(`Sync Gomag finalizat · ${r.data.upserted}/${r.data.fetched} produse${r.data.variations ? ` (${r.data.variations} variații)` : ""}`);
      await load();
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setSyncing(false); }
  };

  const handlePushOblioStock = async () => {
    setPushingStock(true); setError(""); setSuccess("");
    try {
      const r = await axios.post(`${API}/products/push-oblio-stock-to-gomag`);
      const reservations = r.data.reservations_snapshot ?? 0;
      setSuccess(`Stoc Oblio trimis la Gomag · ${r.data.pushed} actualizate · ${r.data.skipped} skipped · ${reservations} rezervări active`);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setPushingStock(false); }
  };

  const [pushingPrices, setPushingPrices] = useState(false);
  const [pricePushProgress, setPricePushProgress] = useState("");
  const handlePushGomagPrices = async () => {
    if (!window.confirm("Se vor trimite la Gomag TOATE prețurile setate în platformă. Rulează în fundal ~2-3 minute. Continui?")) return;
    setPushingPrices(true); setError(""); setSuccess(""); setPricePushProgress("Pornire…");
    try {
      const startRes = await axios.post(`${API}/products/bulk/push-gomag-prices`);
      if (startRes.data?.status === "failed") { setError("Nu s-a putut porni bulk push"); return; }
      // Poll status every 3s until done.
      let done = false;
      while (!done) {
        await new Promise((r) => setTimeout(r, 3000));
        const s = await axios.get(`${API}/products/bulk/push-gomag-prices/status`);
        const d = s.data || {};
        setPricePushProgress(d.progress || "…");
        if (d.status === "done") {
          done = true;
          if ((d.error_count || 0) > 0) {
            setError(`Push Gomag: ${d.pushed}/${d.candidates} OK · ${d.error_count} erori (ex: ${d.errors?.[0]?.sku}: ${d.errors?.[0]?.error})`);
          } else {
            setSuccess(`Prețuri Gomag actualizate · ${d.pushed}/${d.candidates} produse sincronizate`);
          }
        } else if (d.status === "failed") {
          done = true;
          setError("Bulk push Gomag eșuat: " + (d.error || "unknown"));
        }
      }
    } catch (e) {
      setError("Push prețuri Gomag eșuat: " + (e.response?.data?.detail || e.message));
    } finally { setPushingPrices(false); setPricePushProgress(""); }
  };

  const handlePullOblioStock = async () => {
    setPullingOblio(true); setError(""); setSuccess("");
    try {
      const r = await axios.post(`${API}/products/sync-oblio-stock`);
      if (!r.data?.ok) {
        setError(`Pull Oblio eșuat · ${r.data?.reason || "necunoscut"}`);
      } else {
        setSuccess(`Pull Oblio (${r.data.warehouse}) · ${r.data.updated} actualizate · ${r.data.unchanged} neschimbate · ${r.data.not_found_count} SKU lipsesc în catalog`);
        await load();
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setPullingOblio(false); }
  };

  const handleRegisterWebhook = async () => {
    setError(""); setSuccess("");
    try {
      const r = await axios.post(`${API}/oblio/webhooks/register`);
      if (r.data?.already) setSuccess(`Webhook Oblio deja înregistrat la: ${r.data.endpoint}`);
      else setSuccess(`Webhook Oblio înregistrat cu succes la: ${r.data.endpoint} — sync-ul stocului va fi INSTANT`);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  useEffect(() => { load(); }, [load]);

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8" data-testid="products-page">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-zinc-50">Produse</h1>
          <p className="text-sm text-zinc-500">Catalog Shopify (bragus.ro)</p>
        </div>
      </div>

      <ProductsShopifyTab onOpenProduct={(shadowProductId) => {
        if (!shadowProductId) return;
        setEditProduct({ id: shadowProductId, custom: {}, _source: "gomag" });
      }} />

      <ProductEditModal product={editProduct} onClose={() => setEditProduct(null)} onSaved={load} />
    </div>
  );
}


// ───────────────────────────────────────────────────────────────────────────
// Opportunities Modal: Gomag products NOT yet listed on a given marketplace.
// Helps the user identify "where could I expand?" by surfacing products with
// stock + linked offers elsewhere that are MISSING from a target marketplace.
// ───────────────────────────────────────────────────────────────────────────
function OpportunitiesModal({ onClose }) {
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("emag_bg_bau");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const FILTER_OPTIONS = [
    { v: "", label: "Toate (orice marketplace lipsă)" },
    { v: "emag_ro_bau", label: "🇷🇴 Lipsă pe eMAG RO BAU" },
    { v: "emag_ro_fbe", label: "🇷🇴 Lipsă pe eMAG RO FBE" },
    { v: "emag_bg_bau", label: "🇧🇬 Lipsă pe eMAG BG BAU" },
    { v: "emag_bg_fbe", label: "🇧🇬 Lipsă pe eMAG BG FBE" },
    { v: "emag_hu_bau", label: "🇭🇺 Lipsă pe eMAG HU BAU" },
    { v: "emag_hu_fbe", label: "🇭🇺 Lipsă pe eMAG HU FBE" },
    { v: "trendyol_ro", label: "🇷🇴 Lipsă pe Trendyol RO" },
    { v: "trendyol_bg", label: "🇧🇬 Lipsă pe Trendyol BG" },
  ];

  const load = async () => {
    setLoading(true); setError("");
    try {
      const params = filter ? { integration_key: filter } : {};
      const r = await axios.get(`${API}/products/missing-offers`, { params });
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filter]);

  const FLAGS = {
    emag_ro_bau: "🇷🇴 RO BAU", emag_bg_bau: "🇧🇬 BG BAU", emag_hu_bau: "🇭🇺 HU BAU",
    emag_ro_fbe: "🇷🇴 RO FBE", emag_bg_fbe: "🇧🇬 BG FBE", emag_hu_fbe: "🇭🇺 HU FBE",
    trendyol_ro: "🇷🇴 Trendyol RO", trendyol_bg: "🇧🇬 Trendyol BG",
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose} data-testid="opportunities-modal">
      <div className="w-full max-w-5xl rounded-xl border border-zinc-800 bg-zinc-950 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <header className="flex items-center justify-between border-b border-zinc-800 px-5 py-3">
          <div>
            <h3 className="text-sm font-semibold text-zinc-100">Oportunități — Produse Gomag fără ofertă pe Marketplace</h3>
            <p className="text-[11px] text-zinc-500">
              Identifică unde poți extinde catalogul. Folosește filtrul pentru a viza un marketplace specific.
            </p>
          </div>
          <button onClick={onClose} data-testid="opportunities-close" className="rounded p-1 text-zinc-400 hover:text-zinc-100">
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="p-5">
          <div className="mb-4 flex items-center gap-2">
            <label className="text-xs font-medium text-zinc-400">Filtru:</label>
            <select value={filter} onChange={(e) => setFilter(e.target.value)}
              data-testid="opportunities-filter"
              className="rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-sm text-zinc-100 outline-none focus:border-orange-500/50">
              {FILTER_OPTIONS.map((opt) => (
                <option key={opt.v} value={opt.v}>{opt.label}</option>
              ))}
            </select>
          </div>
          {error && <div className="mb-3 rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">{error}</div>}
          {loading ? (
            <p className="px-3 py-6 text-center text-xs text-zinc-500">Se încarcă...</p>
          ) : !data || data.count === 0 ? (
            <div className="rounded-lg border border-dashed border-emerald-500/40 bg-emerald-500/5 px-6 py-8 text-center" data-testid="opportunities-empty">
              <CheckCircle2 className="mx-auto mb-2 h-8 w-8 text-emerald-400" />
              <p className="text-sm font-semibold text-emerald-200">Nicio oportunitate nouă!</p>
              <p className="mt-1 text-[11px] text-emerald-300/70">Toate produsele Gomag au cel puțin o ofertă pe marketplace-ul selectat.</p>
            </div>
          ) : (
            <>
              <p className="mb-2 text-xs text-zinc-400">
                <span className="font-bold text-zinc-100">{data.count}</span> produse fără ofertă{filter ? ` pe ${FLAGS[filter] || filter}` : " pe cel puțin un marketplace"}.
              </p>
              <div className="max-h-[60vh] overflow-y-auto" data-testid="opportunities-list">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 z-10 bg-zinc-900">
                    <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                      <th className="px-3 py-2">Produs</th>
                      <th className="px-3 py-2 w-24">Stoc Oblio</th>
                      <th className="px-3 py-2">Prezent pe</th>
                      <th className="px-3 py-2">Lipsă de pe</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-800/80">
                    {(data.products || []).map((p) => (
                      <tr key={p.id} className="hover:bg-zinc-900/40" data-testid={`opportunity-row-${p.id}`}>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            {p.main_image ? (
                              <img src={p.main_image} alt="" loading="lazy"
                                className="h-10 w-10 flex-shrink-0 rounded border border-zinc-800 bg-zinc-900 object-contain" />
                            ) : <div className="h-10 w-10 flex-shrink-0 rounded border border-zinc-800 bg-zinc-900" />}
                            <div className="min-w-0">
                              <p className="truncate text-zinc-100" title={p.name}>{p.name}</p>
                              <p className="font-mono text-[10px] text-sky-300">{p.sku}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
                            (p.stoc_oblio || 0) > 0 ? "bg-emerald-500/15 text-emerald-200" : "bg-rose-500/15 text-rose-200"
                          }`}>{p.stoc_oblio ?? "—"}</span>
                        </td>
                        <td className="px-3 py-2">
                          {(p.present_on || []).length === 0 ? <span className="text-zinc-600">—</span> :
                            <div className="flex flex-wrap gap-1">
                              {p.present_on.map((k) => <span key={k} className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] text-emerald-200">{FLAGS[k] || k}</span>)}
                            </div>}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap gap-1">
                            {(p.missing_from || []).map((k) => <span key={k} className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] text-amber-200">{FLAGS[k] || k}</span>)}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

